import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import EditRoundedIcon from '@mui/icons-material/EditRounded';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { ErrorState, LoadingState } from '@/shared/ui';

import { usePutRoundingPolicy, useRoundingPolicies } from '../../hooks';
import { ROUNDING_MODE_LABELS, ROUNDING_SCOPE_LABELS } from '../../model/options';
import type { RoundingMode, RoundingPolicyResponse, RoundingScope } from '../../model/types';
import type { ToastSeverity } from '../../lib/useToast';

/** The only currency this platform prices in today — see design spec §3 "Currency (RUB; more when served)". */
const CURRENCY = 'RUB';
const SCOPES: RoundingScope[] = ['LIST_PRICE', 'DISCOUNT_RESULT'];
const MODES: RoundingMode[] = ['HALF_UP', 'UP', 'DOWN'];

interface RoundingPolicyDialogProps {
  open: boolean;
  scope: RoundingScope;
  existing?: RoundingPolicyResponse;
  pending?: boolean;
  onClose: () => void;
  onSave: (mode: RoundingMode, increment: number, ending?: string) => void;
}

function RoundingPolicyDialog({ open, scope, existing, pending = false, onClose, onSave }: RoundingPolicyDialogProps) {
  const { t } = useTranslation('catalog');
  const [mode, setMode] = useState<RoundingMode>('HALF_UP');
  const [increment, setIncrement] = useState('0.01');
  const [ending, setEnding] = useState('');

  useEffect(() => {
    if (!open) return;
    setMode(existing?.mode ?? 'HALF_UP');
    setIncrement(existing ? String(existing.increment) : '0.01');
    setEnding(existing?.ending ?? '');
  }, [open, existing]);

  const parsedIncrement = Number.parseFloat(increment);
  const valid = increment.trim() !== '' && !Number.isNaN(parsedIncrement) && parsedIncrement > 0;

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t(ROUNDING_SCOPE_LABELS[scope] ?? scope)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField select label={t('roundingPanel.modeLabel')} value={mode} onChange={(e) => setMode(e.target.value as RoundingMode)} autoFocus>
            {MODES.map((m) => (
              <MenuItem key={m} value={m}>
                {t(ROUNDING_MODE_LABELS[m] ?? m)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('roundingPanel.incrementLabel')}
            type="number"
            value={increment}
            onChange={(e) => setIncrement(e.target.value)}
            helperText={t('roundingPanel.incrementHelper')}
            error={increment !== '' && !valid}
            slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
          />
          {scope === 'LIST_PRICE' ? (
            <TextField
              label={t('roundingPanel.endingLabel')}
              value={ending}
              onChange={(e) => setEnding(e.target.value)}
              helperText={t('roundingPanel.endingHelper')}
              placeholder=".99"
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!valid || pending}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() => onSave(mode, parsedIncrement, scope === 'LIST_PRICE' ? ending.trim() || undefined : undefined)}
        >
          {t('common:action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export interface RoundingPoliciesCardProps {
  canManage: boolean;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/**
 * The "Price rounding" card (design spec §8.11, adr-pricing-022) — one row per (currency, scope),
 * a currency this platform only ever prices in as RUB today. Deliberately distinct from the
 * discount-rounding policy card next to it (a basket calculation concern, not a price-line one).
 */
export function RoundingPoliciesCard({ canManage, onResult }: RoundingPoliciesCardProps) {
  const { t } = useTranslation('catalog');
  const query = useRoundingPolicies();
  const putMutation = usePutRoundingPolicy();
  const [editingScope, setEditingScope] = useState<RoundingScope | null>(null);

  const byScope = new Map((query.data ?? []).map((p) => [p.scope, p] as const));

  const handleSave = (mode: RoundingMode, increment: number, ending?: string) => {
    if (!editingScope) return;
    putMutation.mutate(
      { currency: CURRENCY, scope: editingScope, mode, increment, ending },
      {
        onSuccess: () => {
          setEditingScope(null);
          onResult('success', t('roundingPanel.saved'));
        },
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {t('roundingPanel.priceTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('roundingPanel.priceHelper')}
        </Typography>

        {query.isLoading ? (
          <LoadingState label={t('roundingPanel.loading')} fullHeight={false} />
        ) : query.isError ? (
          <ErrorState message={query.error?.message} onRetry={() => void query.refetch()} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('roundingPanel.table.currency')}</TableCell>
                <TableCell>{t('roundingPanel.table.scope')}</TableCell>
                <TableCell>{t('roundingPanel.table.mode')}</TableCell>
                <TableCell align="right">{t('roundingPanel.table.increment')}</TableCell>
                <TableCell align="right">{t('roundingPanel.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SCOPES.map((scope) => {
                const policy = byScope.get(scope);
                const editButton = (
                  <IconButton
                    size="small"
                    aria-label={t('roundingPanel.editAria', { scope: t(ROUNDING_SCOPE_LABELS[scope]) })}
                    disabled={!canManage}
                    onClick={() => setEditingScope(scope)}
                  >
                    <EditRoundedIcon fontSize="small" />
                  </IconButton>
                );
                return (
                  <TableRow key={scope} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: '"JetBrains Mono", monospace' }}>
                        {CURRENCY}
                      </Typography>
                    </TableCell>
                    <TableCell>{t(ROUNDING_SCOPE_LABELS[scope])}</TableCell>
                    <TableCell>
                      {policy ? t(ROUNDING_MODE_LABELS[policy.mode] ?? policy.mode) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('roundingPanel.notConfigured')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {policy ? policy.increment : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {canManage ? editButton : (
                        <Tooltip title={t('priceListsPanel.noManagePermissionTooltip')}>
                          <span>{editButton}</span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <RoundingPolicyDialog
        open={editingScope !== null}
        scope={editingScope ?? 'LIST_PRICE'}
        existing={editingScope ? byScope.get(editingScope) : undefined}
        pending={putMutation.isPending}
        onClose={() => setEditingScope(null)}
        onSave={handleSave}
      />
    </Card>
  );
}
